import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@apollo/client';
import { GET_CAR, UPDATE_CAR } from '../../services/carService/queries';
import { Button } from '../button';
import NumericInput from '../NumericInput';

const Container = styled.div`
  ${tw`p-6 max-w-md`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-2 mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const InfoIcon = styled(FontAwesomeIcon)`
  ${tw`text-text-tertiary cursor-help`}
`;

const Section = styled.div`
  ${tw`mb-6`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4`}
`;

const LearnMore = styled.a`
  ${tw`text-black cursor-pointer mb-6 inline-block`}
  &:hover {
    ${tw`text-black`}
  }
`;

const InputGroup = styled.div`
  ${tw`mb-6`}
`;

const InputLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-1`}
`;

const Input = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md appearance-none bg-white`}

  &:focus {
    ${tw`outline-none ring-2 ring-black border-black`}
  }
`;

const TipBox = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-lg flex items-start gap-3 mb-6`}
`;

const SaveButtonContainer = styled.div`
  ${tw`w-full py-2`}
`;

interface DepositAmountProps {
  carId: string;
  deposit?: number | null | undefined;
}

const DepositAmount: React.FC<DepositAmountProps> = ({ carId, deposit }) => {
  const [depositAmount, setDepositAmount] = useState<number>(
    deposit !== null && deposit !== undefined ? deposit : 500,
  );

  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      toast.success('Security deposit amount updated successfully');
    },
    onError: error => {
      toast.error('Error updating security deposit amount');
    },
  });

  const handleSave = async () => {
    if (depositAmount < 0) {
      toast.error('Deposit amount cannot be negative');
      return;
    }

    await updateCar({
      variables: {
        id: carId,
        updateCarInput: {
          deposit: depositAmount,
        },
      },
    });
  };

  return (
    <Container>
      <Header>
        <Title>Security deposit</Title>
        <InfoIcon icon={faInfoCircle} />
      </Header>

      <Section>
        <Description>
          Set the security deposit amount for this vehicle. This amount will be held or charged when
          guests book this car. A higher deposit can help protect against potential damages.
        </Description>
        <LearnMore>Learn more</LearnMore>

        <InputGroup>
          <InputLabel>Deposit amount ($)</InputLabel>
          <NumericInput
            value={depositAmount}
            onValueChange={(value: number | null) => setDepositAmount(value ?? 500)}
            placeholder="500"
            minValue={0}
          />
        </InputGroup>

        <TipBox>
          <FontAwesomeIcon icon={faInfoCircle} tw="text-black mt-1" />
          <p>
            The default deposit is $500. You can adjust this amount based on your vehicle's value
            and risk assessment.
          </p>
        </TipBox>

        <SaveButtonContainer>
          <Button text="Save" onClick={handleSave} isDisabled={loading} isLoading={loading} />
        </SaveButtonContainer>
      </Section>
    </Container>
  );
};

export default DepositAmount;
